import { Download, Eye, FileText, ShieldCheck, X } from 'lucide-react';
import { useState } from 'react';

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(15, 23, 42, 0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10000,
  padding: '24px'
};

const modalStyle = {
  background: 'white',
  borderRadius: '16px',
  width: 'min(1100px, 100%)',
  maxHeight: '90vh',
  boxShadow: '0 20px 60px rgba(15, 23, 42, 0.25)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
};

const sourceBadgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '4px 8px',
  borderRadius: '999px',
  fontSize: '12px',
  fontWeight: 600
};

function formatDate(value) {
  if (!value) return 'Data non disponibile';
  return new Date(value).toLocaleString('it-IT', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function slugify(value) {
  return (value || 'documento')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

async function downloadConsentPdf(consent, userName) {
  const { default: jsPDF } = await import('jspdf');
  const html2canvas = (await import('html2canvas')).default;

  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.width = '800px';
  container.style.padding = '40px';
  container.style.background = '#ffffff';
  container.style.fontFamily = 'Arial, sans-serif';
  container.style.lineHeight = '1.6';
  container.style.color = '#1f2937';
  container.innerHTML = `
    <div>
      <h1 style="margin: 0 0 24px; font-size: 28px;">${consent.title || 'Documento'}</h1>
      ${consent.html || '<p><em>Contenuto non disponibile.</em></p>'}
      <div style="margin-top: 40px; padding-top: 20px; border-top: 2px solid #111827;">
        <p><strong>Utente:</strong> ${userName || 'N/D'}</p>
        <p><strong>Data accettazione:</strong> ${formatDate(consent.acceptedAt)}</p>
        <p><strong>Versione:</strong> ${consent.version || 'N/D'}</p>
        <p><strong>Metodo consenso:</strong> ${consent.consentMethod || 'N/D'}</p>
        <p><strong>Origine contenuto:</strong> ${consent.source === 'SNAPSHOT' ? 'Snapshot salvato al momento dell\'accettazione' : 'Fallback documento associato (no snapshot)'}</p>
      </div>
    </div>
  `;

  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      logging: false,
      windowWidth: 800
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth - 20;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 10;

    pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight + 10;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(`${slugify(consent.title)}-v${consent.version}-${slugify(userName)}.pdf`);
  } finally {
    document.body.removeChild(container);
  }
}

/**
 * InviteConsentsModal
 *
 * Props:
 *   open        {boolean}  Whether the modal is visible
 *   userName    {string}   Display name of the user (fullName or email)
 *   userEmail   {string}   Email of the user (shown in header)
 *   consents    {Array}    Array of consent objects from /user-consents endpoint
 *   onClose     {function} Callback to close the modal
 */
const InviteConsentsModal = ({ open, userName, userEmail, consents = [], onClose }) => {
  const [previewConsent, setPreviewConsent] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  if (!open) return null;

  const displayName = userName || userEmail || 'Utente';
  const hasFallback = consents.some((c) => c.source === 'FALLBACK');

  const handleDownload = async (consent) => {
    if (!consent?.html) return;
    try {
      setDownloadingId(consent.id);
      await downloadConsentPdf(consent, displayName);
    } catch (err) {
      console.error('Error downloading consent PDF:', err);
      alert('Errore durante la generazione del PDF. Riprova.');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: '24px 28px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
          <div>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px', color: '#0f172a' }}>
              <ShieldCheck size={22} />
              Consensi accettati
            </h3>
            <p style={{ margin: '8px 0 0', color: '#475569' }}>
              <strong>{displayName}</strong>
              {userEmail && userName && userName !== userEmail && (
                <span style={{ color: '#94a3b8', marginLeft: '8px' }}>— {userEmail}</span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748b', padding: '4px' }}
            title="Chiudi"
          >
            <X size={22} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px 28px', overflowY: 'auto' }}>
          {consents.length === 0 && (
            <div style={{ padding: '24px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', color: '#475569' }}>
              Nessun consenso registrato per questo utente.
            </div>
          )}

          {consents.length > 0 && (
            <div style={{ display: 'grid', gap: '14px' }}>
              {consents.map((consent) => {
                const canRender = Boolean(consent.html);
                const isSnapshot = consent.source === 'SNAPSHOT';
                const isExpanded = expandedIds.has(consent.id);
                const hasExtras = consent.consentMethod || consent.snapshotSha256 || consent.ip || consent.userAgent || consent.legalDocumentId;

                return (
                  <div
                    key={consent.id}
                    style={{
                      border: '1px solid #e2e8f0',
                      borderRadius: '14px',
                      padding: '18px 20px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '16px',
                      alignItems: 'flex-start',
                      background: '#ffffff'
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <FileText size={18} color="#334155" />
                        <strong style={{ color: '#0f172a' }}>{consent.title || 'Documento'}</strong>
                        <span style={{ color: '#64748b', fontSize: '14px' }}>(v{consent.version})</span>
                        <span
                          style={{
                            ...sourceBadgeStyle,
                            background: isSnapshot ? '#dcfce7' : consent.source === 'FALLBACK' ? '#fef3c7' : '#fee2e2',
                            color: isSnapshot ? '#166534' : consent.source === 'FALLBACK' ? '#92400e' : '#991b1b'
                          }}
                        >
                          {isSnapshot ? 'Snapshot audit' : consent.source === 'FALLBACK' ? 'Fallback (no snapshot)' : 'Non disponibile'}
                        </span>
                      </div>

                      <div style={{ marginTop: '10px', color: '#475569', fontSize: '14px', display: 'grid', gap: '4px' }}>
                        <div>
                          <strong>Tipo:</strong>{' '}
                          {consent.category === 'CONTRACT' ? 'Contratto' : 'Consenso'}
                        </div>
                        <div><strong>Accettato il:</strong> {formatDate(consent.acceptedAt)}</div>

                        {hasExtras && (
                          <div style={{ marginTop: '8px' }}>
                            <button
                              onClick={() => toggleExpanded(consent.id)}
                              style={{
                                background: 'none', border: 'none', padding: 0,
                                cursor: 'pointer', color: '#3b82f6', fontSize: '13px',
                                display: 'flex', alignItems: 'center', gap: '4px'
                              }}
                            >
                              {isExpanded ? '▲' : '▼'} {isExpanded ? 'Nascondi dettagli' : 'Ulteriori informazioni'}
                            </button>

                            {isExpanded && (
                              <div style={{ marginTop: '8px', display: 'grid', gap: '4px', paddingLeft: '8px', borderLeft: '2px solid #e2e8f0' }}>
                                {consent.consentMethod && (
                                  <div><strong>Metodo consenso:</strong> {consent.consentMethod}</div>
                                )}
                                {consent.snapshotSha256 && (
                                  <div style={{ fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all' }}>
                                    <strong>SHA-256:</strong> {consent.snapshotSha256}
                                  </div>
                                )}
                                {consent.ip && (
                                  <div><strong>IP:</strong> {consent.ip}</div>
                                )}
                                {consent.userAgent && (
                                  <div style={{ fontSize: '12px', color: '#94a3b8', wordBreak: 'break-all' }}>
                                    <strong style={{ color: '#475569' }}>User-Agent:</strong> {consent.userAgent}
                                  </div>
                                )}
                                {consent.legalDocumentId && (
                                  <div style={{ fontSize: '12px', color: '#94a3b8', fontFamily: 'monospace' }}>
                                    <strong style={{ color: '#475569' }}>Doc ID:</strong> {consent.legalDocumentId}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '10px', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => setPreviewConsent(consent)}
                        disabled={!canRender}
                        className="btn"
                        style={{ margin: 0, padding: '10px 16px', opacity: canRender ? 1 : 0.5, cursor: canRender ? 'pointer' : 'not-allowed' }}
                      >
                        <Eye size={16} /> Vedi
                      </button>
                      <button
                        onClick={() => handleDownload(consent)}
                        disabled={!canRender || downloadingId === consent.id}
                        className="btn"
                        style={{
                          margin: 0,
                          padding: '10px 16px',
                          background: 'linear-gradient(135deg, #0f766e 0%, #115e59 100%)',
                          opacity: canRender ? 1 : 0.5,
                          cursor: canRender ? 'pointer' : 'not-allowed'
                        }}
                      >
                        <Download size={16} /> {downloadingId === consent.id ? 'Scaricamento...' : 'Scarica PDF'}
                      </button>
                    </div>
                  </div>
                );
              })}

              {hasFallback && (
                <div
                  style={{
                    marginTop: '4px',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: '1px solid #fde68a',
                    background: '#fffbeb',
                    color: '#92400e',
                    fontSize: '13px'
                  }}
                >
                  Nota: "Fallback (no snapshot)" indica che per quella accettazione non era presente uno snapshot HTML salvato.
                  Viene mostrato il documento legale collegato al legalDocumentId associato all'accettazione.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Preview sub-modal */}
        {previewConsent && (
          <div style={overlayStyle} onClick={() => setPreviewConsent(null)}>
            <div
              style={{ ...modalStyle, width: 'min(1200px, 100%)', maxHeight: '92vh' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'center' }}>
                <div>
                  <h4 style={{ margin: 0, color: '#0f172a' }}>{previewConsent.title}</h4>
                  <p style={{ margin: '6px 0 0', color: '#64748b' }}>
                    Versione {previewConsent.version} · {formatDate(previewConsent.acceptedAt)}
                  </p>
                </div>
                <button
                  onClick={() => setPreviewConsent(null)}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748b', padding: '4px' }}
                  title="Chiudi anteprima"
                >
                  <X size={22} />
                </button>
              </div>
              <div style={{ overflowY: 'auto', padding: '24px', background: '#f8fafc' }}>
                <div style={{ maxWidth: '900px', margin: '0 auto', background: '#fff', padding: '32px', borderRadius: '12px', boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)' }}>
                  <div dangerouslySetInnerHTML={{ __html: previewConsent.html }} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InviteConsentsModal;
